/**
 * Store Pinia des codes stock (Lot 5 - E13-F02-U06)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { ICodeStock } from "../models/codeStock";
import { ICodeStockApiResponse } from "../models/api/codeStockApiResponse";
import { ICodeStockFilter } from "../models/filters/codeStockFilter";
import { ICodeStockForm } from "../models/forms/codeStockForm";
import CodeStockService from "../services/CodeStockService";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import { useUsersStore } from "./users.store";
import { useParameterStore } from "./parameter.store";

export const useCodeStockStore = defineStore("codeStocks", () => {
    // State
    const datas = ref<ICodeStock[]>([]);
    const current = ref<ICodeStock>({} as ICodeStock);
    const itemForm = ref<ICodeStockForm>({} as ICodeStockForm);
    const isLoading = ref(false);

    // Getters

    /** Libellé affiché pour un code stock : le code du centre courant est remplacé par "CEN" */
    function getDisplayLabel(cs: ICodeStock): string {
        const centerCode = useUsersStore().currentCenter?.code;
        const displayCode = cs.code === centerCode ? "CEN" : cs.code;
        return `${displayCode} — ${cs.libelle?.toUpperCase() ?? ""}`;
    }

    /** Vérifie si un code stock est de type "Site de production" (via parameterStore ou relation) */
    function isSiteType(cs: ICodeStock): boolean {
        if (cs.codeStockType?.code3 === "SITE") return true;
        if (cs.codeStockTypeId) {
            const param = useParameterStore().datas.find(p => p.id === cs.codeStockTypeId);
            return param?.code3 === "SITE";
        }
        return false;
    }

    function sortCodeStock(a: ICodeStock, b: ICodeStock): number {
        const aIsSite = isSiteType(a);
        const bIsSite = isSiteType(b);
        if (aIsSite && !bIsSite) return -1;
        if (!aIsSite && bIsSite) return 1;
        const aIsNum = /^\d+$/.test(a.code);
        const bIsNum = /^\d+$/.test(b.code);
        if (aIsNum && !bIsNum) return -1;
        if (!aIsNum && bIsNum) return 1;
        if (aIsNum && bIsNum) return Number(a.code) - Number(b.code);
        return a.code.localeCompare(b.code);
    }

    const getSorted = computed((): ICodeStock[] => {
        return [...datas.value].sort(sortCodeStock);
    });

    /** Codes stock rattachés au centre courant, triés : code SITE du centre en 1er, puis les autres */
    const getSortedForCenter = computed((): ICodeStock[] => {
        const center   = useUsersStore().currentCenter;
        const centerId = center?.id;
        const filtered = getSorted.value.filter(cs => cs.centerIds?.includes(centerId));
        const centerCode = filtered.filter(cs => isSiteType(cs));
        const rest       = filtered.filter(cs => !isSiteType(cs));
        return [...centerCode, ...rest];
    });

    /** Codes stock rattachés à un centre */
    const getByCenter = computed(() => (centerId: number): ICodeStock[] => {
        return datas.value
            .filter(cs => cs.centerIds?.includes(centerId))
            .sort(sortCodeStock);
    });

    // Actions

    /** Hydrate codeStockType depuis le parameterStore pour chaque code stock */
    function hydrate(items: ICodeStock[]): ICodeStock[] {
        const params = useParameterStore().datas;
        if (params.length === 0) return items;
        for (const cs of items) {
            if (!cs.codeStockType && cs.codeStockTypeId) {
                cs.codeStockType = params.find(p => p.id === cs.codeStockTypeId);
            }
        }
        return items;
    }

    async function load(filter: ICodeStockFilter) {
        isLoading.value = true;
        const result = await CodeStockService.getAll<ICodeStockFilter, ICodeStockApiResponse>(filter);
        if (!result.error) {
            datas.value = hydrate(result.data as ICodeStock[]);
        } else {
            datas.value = [];
        }
        isLoading.value = false;
        return result;
    }

    async function get(id: number) {
        const result = await CodeStockService.getById<ICodeStockApiResponse>(id);
        if (!result.error) {
            const model = result.data as ICodeStock;
            const index = datas.value.findIndex(a => a.id === id);
            if (index >= 0)
                datas.value[index] = model;
            else
                datas.value.push(model);
            current.value = model;
            itemForm.value = toForm(model);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find(objet => objet.id === id)!;
        itemForm.value = toForm(current.value as ICodeStock);
        return current.value;
    }

    async function save(maj: boolean = true) {
        let result: ServiceOperationResult<ICodeStock | IDataOperationResult<ICodeStock>>;
        if (itemForm.value.id > 0) {
            result = await CodeStockService.update(itemForm.value);
            if (!result.error) {
                current.value = result.data as ICodeStock;
                itemForm.value = toForm(current.value);
                if (maj) {
                    const index = datas.value.findIndex(a => a.id === itemForm.value.id);
                    if (index >= 0)
                        datas.value[index] = current.value;
                }
            }
        } else {
            result = await CodeStockService.create(itemForm.value);
            if (!result.error) {
                current.value = result.data as ICodeStock;
                itemForm.value = toForm(current.value);
                // Eviter le doublon avec SignalR qui peut arriver avant
                if (datas.value.findIndex(a => a.id === current.value.id) === -1) {
                    datas.value.push(current.value);
                }
            }
        }
        return result;
    }

    async function deleteCodeStock(id: number) {
        const result = await CodeStockService.delete<ICodeStock>(id);
        if (!result.error) {
            const index = datas.value.findIndex(a => a.id === id);
            if (index >= 0)
                datas.value.splice(index, 1);
        }
        return result;
    }

    function toForm(item: ICodeStock): ICodeStockForm {
        const form = {} as ICodeStockForm;
        form.id = item.id;
        form.code = item.code;
        form.libelle = item.libelle;
        form.sortOrder = item.sortOrder;
        form.address = item.address;
        form.city = item.city;
        form.postalCode = item.postalCode;
        form.codeStockTypeId = item.codeStockType?.id ?? item.codeStockTypeId;
        form.centerIds = item.centerIds ?? [];
        return form;
    }

    function newForm(lienId?: number): ICodeStockForm {
        itemForm.value = {
            id: 0,
            code: "",
            libelle: "",
            sortOrder: 0,
            address: "",
            city: "",
            postalCode: "",
            codeStockTypeId: undefined,
            centerIds: lienId ? [lienId] : [],
        } as ICodeStockForm;
        return itemForm.value;
    }

    function newItem(): ICodeStock {
        current.value = {
            id: 0,
            code: "",
            libelle: "",
            sortOrder: 0,
            centerIds: [],
        } as ICodeStock;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        datas, current, itemForm, isLoading,
        getSorted, getSortedForCenter, getByCenter, isSiteType, getDisplayLabel,
        load, get, searchStoreById, save, delete: deleteCodeStock,
        toForm, newForm, newItem,
    };
});

// SignalR
signalRManager.connection.on("MajCodeStock", (payload: IPayloadStore) => {
    const store = useCodeStockStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex(a => a.id === payload.data?.id) === -1) {
                store.datas.push(payload.data as ICodeStock);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex(a => a.id === payload.data?.id);
            if (idx > -1) store.datas.splice(idx, 1, payload.data as ICodeStock);
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex(a => a.id === payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
